// PO1-3487: destinatário da NF (CNPJ/CPF, razão social, endereço) e
// complemento do local de entrega na cotação. Os campos de destino de sempre
// (CEP, cidade, UF) são o local de entrega e não mudam de significado.

import api from "services/api";

export const soDigitos = (v) => String(v || "").replace(/\D/g, "");

// 00.000.000/0000-00 ou 000.000.000-00 conforme os dígitos.
export function formatCnpjCpf(v) {
  const d = soDigitos(v).slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1-$2");
  }
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

export function cnpjCpfValido(v) {
  const d = soDigitos(v);
  return !d || d.length === 11 || d.length === 14;
}

// Consulta o CNPJ (BrasilAPI pelo backend) e devolve o destinatário no formato
// da cotação. CPF não tem consulta: devolve null.
export async function buscarDestinatarioPorCnpj(cnpjCpf) {
  const cnpj = soDigitos(cnpjCpf);
  if (cnpj.length !== 14) return null;
  const { data } = await api.get(`/frete/tabelas/cnpj-lookup/${cnpj}`);
  if (!data) return null;
  const tipo = data.descricao_tipo_de_logradouro ? `${data.descricao_tipo_de_logradouro} ` : "";
  const cep = soDigitos(data.cep);
  return {
    razaoSocial: data.razao_social || "",
    logradouro: data.logradouro ? `${tipo}${data.logradouro}`.trim() : "",
    numero: data.numero ? String(data.numero) : "",
    complemento: data.complemento || "",
    bairro: data.bairro || "",
    cep: cep.length === 8 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : cep,
    cidade: data.municipio || "",
    uf: data.uf || "",
  };
}

// Rua e bairro de um CEP (ViaCEP pelo backend). A base própria do Hub
// devolve só cidade/UF; o preview completa a rua quando o CEP tem uma.
// CEP geral de cidade não tem rua: devolve campos vazios.
export async function buscarRuaPorCep(cep) {
  const d = soDigitos(cep);
  if (d.length !== 8) return null;
  try {
    const { data } = await api.get(`/endereco/cep/${d}/preview`);
    if (!data) return null;
    return { logradouro: data.logradouro || "", bairro: data.bairro || "" };
  } catch (e) {
    return null;
  }
}

// formData → payload da API.
export function montarDestinatario(f) {
  const obj = {
    cnpjCpf: soDigitos(f.destCnpjCpf),
    razaoSocial: String(f.destinatario || "").trim(),
    logradouro: String(f.destLogradouro || "").trim(),
    numero: String(f.destNumero || "").trim(),
    complemento: String(f.destComplemento || "").trim(),
    bairro: String(f.destBairro || "").trim(),
    cep: soDigitos(f.destCep),
    cidade: String(f.destCidade || "").trim(),
    uf: String(f.destUf || "").trim().toUpperCase(),
  };
  return Object.values(obj).some(Boolean) ? obj : null;
}

export function montarEnderecoEntrega(f) {
  const obj = {
    nomeLocal: String(f.entregaNomeLocal || "").trim(),
    logradouro: String(f.entregaLogradouro || "").trim(),
    numero: String(f.entregaNumero || "").trim(),
    complemento: String(f.entregaComplemento || "").trim(),
    bairro: String(f.entregaBairro || "").trim(),
  };
  return Object.values(obj).some(Boolean) ? obj : null;
}

// Documento da API → campos do formulário (edição).
export function camposDoDocumento(doc) {
  const d = (doc && doc.destinatario) || {};
  const e = (doc && doc.enderecoEntrega) || {};
  const cep = soDigitos(d.cep);
  return {
    destCnpjCpf: d.cnpjCpf ? formatCnpjCpf(d.cnpjCpf) : "",
    destLogradouro: d.logradouro || "",
    destNumero: d.numero || "",
    destComplemento: d.complemento || "",
    destBairro: d.bairro || "",
    destCep: cep.length === 8 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : cep,
    destCidade: d.cidade || "",
    destUf: d.uf || "",
    entregaNomeLocal: e.nomeLocal || "",
    entregaLogradouro: e.logradouro || "",
    entregaNumero: e.numero || "",
    entregaComplemento: e.complemento || "",
    entregaBairro: e.bairro || "",
  };
}

// Uma linha de endereço para exibição ("Rua X, 10 - Bairro - Cidade/UF").
export function linhaEndereco({ logradouro, numero, complemento, bairro, cidade, uf } = {}) {
  const rua = [logradouro, numero].filter(Boolean).join(", ");
  const partes = [rua, complemento, bairro, [cidade, uf].filter(Boolean).join("/")].filter(Boolean);
  return partes.join(" - ");
}
